/* tslint:disable */
/* eslint-disable */
import { HttpClient, HttpContext, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { filter, map } from 'rxjs/operators';

import { BaseService } from '../base-service';
import { ApiConfiguration } from '../api-configuration';
import { StrictHttpResponse } from '../strict-http-response';
import { RequestBuilder } from '../request-builder';

import { GenericResponse } from '../models/generic-response';
import { GetHandlingStationResponse } from '../models/get-handling-station-response';
import { GetRegistrationCodesResponse } from '../models/get-registration-codes-response';
import { GetWorkOrderResponse } from '../models/get-work-order-response';
import { RecordOrigin } from '../models/record-origin';
import { UpdateWorkOrderRequest } from '../models/update-work-order-request';
import { WorkOrderDetailedResponse } from '../models/work-order-detailed-response';

/**
 * work order APIs
 */
@Injectable({ providedIn: 'root' })
export class WorkOrderService extends BaseService {
  constructor(config: ApiConfiguration, http: HttpClient) {
    super(config, http);
  }

  /** Path part for operation `getAllWorkOrder()` */
  static readonly GetAllWorkOrderPath = '/api/v1/work-orders';

  /**
   * Get wok orders.
   *
   * Return all work orders as pages.
   * The results will be filtered, sorted based on the provided queries.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getAllWorkOrder()` instead.
   *
   * This method doesn't expect any request body.
   */
  getAllWorkOrder$Response(
    params?: {
      /**
       * the page number.
       */
      page?: number;

      /**
       * the size of a page
       */
      size?: number;

      /**
       * sort
       */
      sort?: string;

      /**
       * this adds a string search to the following fields:
       *   - order key
       */
      orderKey?: string;

      /**
       * this adds a string search to the following fields:
       *   - handling office
       */
      handlingOffice?: string;

      /**
       * this adds a string search to the following fields:
       *   - registration code
       */
      registrationCode?: string;

      /**
       * this adds a string search to the following fields:
       *   - from date
       */
      from?: string;

      /**
       * this adds a string search to the following fields:
       *   - to date
       */
      to?: string;

      /**
       * this adds a string search to the following fields:
       *   - client
       */
      clientId?: number;

      /**
       * this add a id search to the following fields:
       *   - invoice id
       */
      invoiceId?: number;

      /**
       * this add a search for work orders not credited
       */
      notCredited?: boolean;

      /**
       * this add a search for work orders include not invoiced
       */
      includeNotInvoiced?: boolean;

      /**
       * When set, limits the list to that origin. Omitted returns both.
       * INTERNAL includes records with no stored origin.
       */
      origin?: RecordOrigin;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<GetWorkOrderResponse>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      WorkOrderService.GetAllWorkOrderPath,
      'get'
    );
    if (params) {
      rb.query('page', params.page, {});
      rb.query('size', params.size, {});
      rb.query('sort', params.sort, {});
      rb.query('orderKey', params.orderKey, {});
      rb.query('handlingOffice', params.handlingOffice, {});
      rb.query('registrationCode', params.registrationCode, {});
      rb.query('from', params.from, {});
      rb.query('to', params.to, {});
      rb.query('clientId', params.clientId, {});
      rb.query('invoiceId', params.invoiceId, {});
      rb.query('notCredited', params.notCredited, {});
      rb.query('includeNotInvoiced', params.includeNotInvoiced, {});
      rb.query('origin', params.origin, {});
    }

    return this.http
      .request(
        rb.build({ responseType: 'json', accept: 'application/json', context })
      )
      .pipe(
        filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<GetWorkOrderResponse>;
        })
      );
  }

  /**
   * Get wok orders.
   *
   * Return all work orders as pages.
   * The results will be filtered, sorted based on the provided queries.
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `getAllWorkOrder$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getAllWorkOrder(
    params?: {
      /**
       * the page number.
       */
      page?: number;

      /**
       * the size of a page
       */
      size?: number;

      /**
       * sort
       */
      sort?: string;

      /**
       * this adds a string search to the following fields:
       *   - order key
       */
      orderKey?: string;

      /**
       * this adds a string search to the following fields:
       *   - handling office
       */
      handlingOffice?: string;

      /**
       * this adds a string search to the following fields:
       *   - registration code
       */
      registrationCode?: string;

      /**
       * this adds a string search to the following fields:
       *   - from date
       */
      from?: string;

      /**
       * this adds a string search to the following fields:
       *   - to date
       */
      to?: string;

      /**
       * this adds a string search to the following fields:
       *   - client
       */
      clientId?: number;

      /**
       * this add a id search to the following fields:
       *   - invoice id
       */
      invoiceId?: number;

      /**
       * this add a search for work orders not credited
       */
      notCredited?: boolean;

      /**
       * this add a search for work orders include not invoiced
       */
      includeNotInvoiced?: boolean;

      /**
       * When set, limits the list to that origin. Omitted returns both.
       * INTERNAL includes records with no stored origin.
       */
      origin?: RecordOrigin;
    },
    context?: HttpContext
  ): Observable<GetWorkOrderResponse> {
    return this.getAllWorkOrder$Response(params, context).pipe(
      map(
        (r: StrictHttpResponse<GetWorkOrderResponse>): GetWorkOrderResponse =>
          r.body
      )
    );
  }

  /** Path part for operation `getWorkOrderDetails()` */
  static readonly GetWorkOrderDetailsPath = '/api/v1/work-orders/{id}';

  /**
   * Get work orders details.
   *
   * Fetch work order details
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getWorkOrderDetails()` instead.
   *
   * This method doesn't expect any request body.
   */
  getWorkOrderDetails$Response(
    params: {
      /**
       * The work order id that needs to be fetched
       */
      id: number;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<WorkOrderDetailedResponse>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      WorkOrderService.GetWorkOrderDetailsPath,
      'get'
    );
    if (params) {
      rb.path('id', params.id, {});
    }

    return this.http
      .request(
        rb.build({ responseType: 'json', accept: 'application/json', context })
      )
      .pipe(
        filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<WorkOrderDetailedResponse>;
        })
      );
  }

  /**
   * Get work orders details.
   *
   * Fetch work order details
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `getWorkOrderDetails$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getWorkOrderDetails(
    params: {
      /**
       * The work order id that needs to be fetched
       */
      id: number;
    },
    context?: HttpContext
  ): Observable<WorkOrderDetailedResponse> {
    return this.getWorkOrderDetails$Response(params, context).pipe(
      map(
        (
          r: StrictHttpResponse<WorkOrderDetailedResponse>
        ): WorkOrderDetailedResponse => r.body
      )
    );
  }

  /** Path part for operation `deleteWorkOrderById()` */
  static readonly DeleteWorkOrderByIdPath = '/api/v1/work-orders/{id}';

  /**
   * Delete work order by id.
   *
   *
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `deleteWorkOrderById()` instead.
   *
   * This method doesn't expect any request body.
   */
  deleteWorkOrderById$Response(
    params: {
      /**
       * The work order id that needs to be deleted
       */
      id: number;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<GenericResponse>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      WorkOrderService.DeleteWorkOrderByIdPath,
      'delete'
    );
    if (params) {
      rb.path('id', params.id, {});
    }

    return this.http
      .request(
        rb.build({ responseType: 'json', accept: 'application/json', context })
      )
      .pipe(
        filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<GenericResponse>;
        })
      );
  }

  /**
   * Delete work order by id.
   *
   *
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `deleteWorkOrderById$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  deleteWorkOrderById(
    params: {
      /**
       * The work order id that needs to be deleted
       */
      id: number;
    },
    context?: HttpContext
  ): Observable<GenericResponse> {
    return this.deleteWorkOrderById$Response(params, context).pipe(
      map((r: StrictHttpResponse<GenericResponse>): GenericResponse => r.body)
    );
  }

  /** Path part for operation `updateWorkOrderById()` */
  static readonly UpdateWorkOrderByIdPath = '/api/v1/work-orders/{id}';

  /**
   * Update work orders.
   *
   * This API updates work orders.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `updateWorkOrderById()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  updateWorkOrderById$Response(
    params: {
      /**
       * The work order id that needs to be updated
       */
      id: number;
      body: UpdateWorkOrderRequest;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<WorkOrderDetailedResponse>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      WorkOrderService.UpdateWorkOrderByIdPath,
      'patch'
    );
    if (params) {
      rb.path('id', params.id, {});
      rb.body(params.body, 'application/json');
    }

    return this.http
      .request(
        rb.build({ responseType: 'json', accept: 'application/json', context })
      )
      .pipe(
        filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<WorkOrderDetailedResponse>;
        })
      );
  }

  /**
   * Update work orders.
   *
   * This API updates work orders.
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `updateWorkOrderById$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  updateWorkOrderById(
    params: {
      /**
       * The work order id that needs to be updated
       */
      id: number;
      body: UpdateWorkOrderRequest;
    },
    context?: HttpContext
  ): Observable<WorkOrderDetailedResponse> {
    return this.updateWorkOrderById$Response(params, context).pipe(
      map(
        (
          r: StrictHttpResponse<WorkOrderDetailedResponse>
        ): WorkOrderDetailedResponse => r.body
      )
    );
  }

  /** Path part for operation `getAllRegistrationCodes()` */
  static readonly GetAllRegistrationCodesPath =
    '/api/v1/work-orders/registration-codes';

  /**
   * Get registration codes.
   *
   * Return all registration codes
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getAllRegistrationCodes()` instead.
   *
   * This method doesn't expect any request body.
   */
  getAllRegistrationCodes$Response(
    params?: {},
    context?: HttpContext
  ): Observable<StrictHttpResponse<GetRegistrationCodesResponse>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      WorkOrderService.GetAllRegistrationCodesPath,
      'get'
    );
    if (params) {
    }

    return this.http
      .request(
        rb.build({ responseType: 'json', accept: 'application/json', context })
      )
      .pipe(
        filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<GetRegistrationCodesResponse>;
        })
      );
  }

  /**
   * Get registration codes.
   *
   * Return all registration codes
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `getAllRegistrationCodes$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getAllRegistrationCodes(
    params?: {},
    context?: HttpContext
  ): Observable<GetRegistrationCodesResponse> {
    return this.getAllRegistrationCodes$Response(params, context).pipe(
      map(
        (
          r: StrictHttpResponse<GetRegistrationCodesResponse>
        ): GetRegistrationCodesResponse => r.body
      )
    );
  }

  /** Path part for operation `getAllHandlingStations()` */
  static readonly GetAllHandlingStationsPath =
    '/api/v1/work-orders/handling-stations';

  /**
   * Get handling stations.
   *
   * Return all handling stations
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getAllHandlingStations()` instead.
   *
   * This method doesn't expect any request body.
   */
  getAllHandlingStations$Response(
    params?: {},
    context?: HttpContext
  ): Observable<StrictHttpResponse<GetHandlingStationResponse>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      WorkOrderService.GetAllHandlingStationsPath,
      'get'
    );
    if (params) {
    }

    return this.http
      .request(
        rb.build({ responseType: 'json', accept: 'application/json', context })
      )
      .pipe(
        filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<GetHandlingStationResponse>;
        })
      );
  }

  /**
   * Get handling stations.
   *
   * Return all handling stations
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `getAllHandlingStations$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getAllHandlingStations(
    params?: {},
    context?: HttpContext
  ): Observable<GetHandlingStationResponse> {
    return this.getAllHandlingStations$Response(params, context).pipe(
      map(
        (
          r: StrictHttpResponse<GetHandlingStationResponse>
        ): GetHandlingStationResponse => r.body
      )
    );
  }
}
